/**
 * 404 im neuen Design: Licht-Kopf wie die anderen Unterseiten, links die Meldung mit
 * Knöpfen, rechts eine Karte mit den wichtigsten Zielen (Bausteine der Support-Seite).
 * Wird auch für unbekannte /shop/…-Adressen genutzt (ProductDetail).
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import FloatingHeader from '../components/layout/FloatingHeader';
import PageMeta from '../components/PageMeta';
import Footer from '../components/Footer';
import Lichtszene from '../components/licht/Lichtszene';
import { SEITEN_BLENDE } from '../components/animations/seitenBlende';
import '../components/startseite/startseite.css';

const ICON = {
  werkzeuge: <svg viewBox="0 0 24 24"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4z" /></svg>,
  abo: <svg viewBox="0 0 24 24"><path d="M12 3l9 5-9 5-9-5z" /><path d="M3 13l9 5 9-5" /></svg>,
  gratis: <svg viewBox="0 0 24 24"><rect x="3" y="8" width="18" height="13" rx="2" /><path d="M12 8v13" /><path d="M3 12h18" /><path d="M12 8c-2-4-6-4-6-1s6 1 6 1 6 2 6-1-4-3-6 1z" /></svg>,
  installer: <svg viewBox="0 0 24 24"><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M4 20h16" /></svg>,
  support: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6V14" /><path d="M12 17.5h.01" /></svg>,
};

export default function NotFound() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const en = i18n.language === 'en' || location.pathname.startsWith('/en');
  const T = (de, e) => (en ? e : de);
  const p = en ? '/en' : '';
  const ziele = [
    [`${p}/shop`, T('Werkzeuge', 'Tools'), T('Alle Extensions für After Effects', 'Every extension for After Effects'), ICON.werkzeuge],
    [`${p}/shop/all-in-one`, 'All-in-One', T('Alle Werkzeuge in einem Abo', 'Every tool in one subscription'), ICON.abo],
    [en ? '/en/free' : '/kostenlos', T('Kostenlos', 'Free'), T('Gratis-Presets und Footage', 'Free presets and footage'), ICON.gratis],
    [`${p}/installer`, 'Installer', T('Alles installieren und aktuell halten', 'Install and update everything'), ICON.installer],
    [`${p}/support`, 'Support', T('Antworten auf häufige Fragen', 'Answers to common questions'), ICON.support],
  ];

  return (
    <motion.div {...SEITEN_BLENDE}>
      {/* Eigene Beschreibung, sonst bliebe die aus index.html bzw. der vorigen Seite stehen */}
      <PageMeta
        title={T('Seite nicht gefunden | DAVIDMEDIA', 'Page not found | DAVIDMEDIA')}
        description={T('Diese Seite gibt es nicht. Weiter zu den Werkzeugen für After Effects, zum All-in-One, zu den kostenlosen Presets, zum Installer oder zum Support.',
          'This page doesn’t exist. Go on to the After Effects tools, the All-in-One, the free presets, the installer or support.')}
        noindex
      />
      <FloatingHeader />
      <div className="startseite">
        <Lichtszene />
        <section className="sek erste voll">
          <div className="innen support-kopf">
            <div>
              <span className="kicker">{T('404 · Seite nicht gefunden', '404 · Page not found')}</span>
              <h1 className="als-h2">
                <span style={{ display: 'block' }}>{T('Diese Seite gibt es nicht.', 'This page doesn’t exist.')}</span>
                <span className="grau" style={{ display: 'block' }}>{T('Vielleicht suchst du das hier.', 'Maybe you’re looking for this.')}</span>
              </h1>
              <p className="lead">{T('Die Adresse führt nirgendwohin. Entweder ein Tippfehler oder die Seite ist umgezogen.',
                'The address leads nowhere. Either a typo or the page has moved.')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 28 }}>
                <Link className="btn primaer" to={en ? '/en' : '/'}>{T('Zur Startseite', 'Back home')}</Link>
                <button type="button" className="btn glas" onClick={() => window.history.back()}>{T('Zurück', 'Go back')}</button>
              </div>
            </div>
            <div className="beliebt">
              <span className="kicker">{T('Beliebte Ziele', 'Popular pages')}</span>
              <ul>
                {ziele.map(([ziel, name, text, icon]) => (
                  <li key={ziel}>
                    <Link to={ziel}>
                      <span className="b-zeichen">{icon}</span>
                      <span className="b-text">{name}<small style={{ display: 'block', marginTop: 2, color: 'var(--t3)', fontWeight: 500, fontSize: 14 }}>{text}</small></span>
                      <span className="b-pfeil" aria-hidden="true">→</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </div>
      <div className="relative z-10"><Footer /></div>
    </motion.div>
  );
}
